/* eslint-disable no-console */
/**
 * Error Log Service
 * Captures client-side errors with rich context and sends to Firestore
 */

import { auth, db } from '@/js/firebase'
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, serverTimestamp, query, where, increment } from 'firebase/firestore'
import { consoleInterceptor } from './consoleInterceptor'
import { logger } from './loggerService'

// Built-in ignore patterns for common benign errors
const BUILT_IN_IGNORE_PATTERNS = [
  { type: 'message', pattern: /404.*asset/i, description: 'Asset existence checks' },
  { type: 'message', pattern: /ResizeObserver loop/i, description: 'Common benign browser error' },
  { type: 'message', pattern: /Loading chunk.*failed/i, description: 'Handled by retry logic' },
  { type: 'message', pattern: /Script error\.?$/i, description: 'Cross-origin script errors without details' },
  { type: 'message', pattern: /NetworkError/i, description: 'Network connectivity issues' },
  { type: 'message', pattern: /^(TypeError: )?Failed to fetch$/i, description: 'Chrome equivalent of NetworkError - bare fetch failure with no app context (dropped connection, tab close)' },
  { type: 'message', pattern: /^AbortError/i, description: 'Expected abort during navigation or unmount' },
  { type: 'message', pattern: /signal is aborted without reason/i, description: 'AbortSignal message text (Firebase SDK cancels in-flight work on navigation) - benign' },
  { type: 'message', pattern: /The user aborted a request/i, description: 'Fetch AbortError message text — expected on navigation/unmount' },
  { type: 'message', pattern: /play\(\) request was interrupted/i, description: 'Media play() promise rejected by a subsequent pause() (player teardown/scrub) - benign' },
  { type: 'message', pattern: /fetching process for the media resource was aborted/i, description: 'Firefox media AbortError on navigation/unmount - benign' },
  { type: 'message', pattern: /Uncaught Error in snapshot listener.*permission-denied/i, description: 'Firestore listener teardown race on sign-out (token revoked before unsubscribe)' },
  { type: 'message', pattern: /Cannot read properties of null.*insertBefore/i, description: 'Vue DOM lifecycle race condition during unmount' },
  { type: 'message', pattern: /Cannot destructure property .bum/i, description: 'Vue internal error during component transition' },
  { type: 'message', pattern: /Cannot read properties of null.*emitsOptions/i, description: 'Vue emit on an unmounted instance (async continuation after navigation) - benign teardown race' },
  { type: 'message', pattern: /Cannot read properties of null.*Sortable/i, description: 'vuedraggable global document handler firing after unmount - benign teardown race' },
  { type: 'message', pattern: /client is offline/i, description: 'Expected offline operation errors' },
  { type: 'message', pattern: /Could not reach Cloud Firestore backend/i, description: 'Firestore offline connectivity' },
  { type: 'message', pattern: /@firebase\/firestore:.*Using maximum backoff delay/i, description: 'Firestore SDK stream backoff notice - SDK self-recovers' },
  { type: 'message', pattern: /@firebase\/firestore:.*resource-exhausted/i, description: 'Firestore SDK listen-stream quota close - SDK retries with backoff; app-level failures log their own errors' },
  { type: 'message', pattern: /Connection to Indexed Database server lost/i, description: 'Safari IndexedDB disconnects on tab suspend - benign' },
  { type: 'message', pattern: /IDBDatabase.*database connection is closing/i, description: 'Firebase persistence transaction during tab teardown - benign' },
  { type: 'message', pattern: /installations\/request-failed/i, description: 'Firebase Installations token fetch (transient 404, SDK self-recovers)' },
  { type: 'message', pattern: /installations\/app-offline/i, description: 'Firebase Installations while browser offline - benign connectivity noise' },
  { type: 'stack', pattern: /at\s+\S*postUserData\s*\(<anonymous>/i, description: 'Browser extension/injected script (postUserData)' },
  { type: 'stack', pattern: /chrome-extension:\/\//i, description: 'Errors thrown from browser extension scripts (e.g. MetaMask inpage.js) - not app code' }
]

class ErrorLogService {
  constructor() {
    this.errorQueue = []
    this.batchInterval = 30000 // 30 seconds
    this.maxQueueSize = 50
    this.fingerprints = new Map() // For deduplication within session (by fingerprint)
    this.recentCoreMessages = new Map() // For short-term dedup across sources (by core message)
    this.recentMessageWindow = 2000 // 2 seconds - window to catch duplicate captures from different sources
    this.rateLimitCounter = 0
    this.rateLimitWindow = 60000 // 1 minute
    this.maxErrorsPerWindow = 10
    this.breadcrumbs = []
    this.maxBreadcrumbs = 20
    this.isSetup = false
    this.batchTimer = null
    this.isCapturing = false // Recursion guard
    this.customIgnorePatterns = [] // Loaded from Firestore
    this.ignorePatternsFetched = false
    this.consoleErrorUnsubscribe = null
  }

  /**
   * Initialize error capturing
   */
  setup() {
    if (this.isSetup) return

    logger.info('[ErrorLogService] Setting up error capture')

    // Load custom ignore patterns from Firestore
    this.loadIgnorePatterns()

    // Set up global error handlers
    this.setupWindowErrorHandler()
    this.setupUnhandledRejectionHandler()
    this.setupBreadcrumbTracking()

    // Hook into console.error via consoleInterceptor
    this.setupConsoleErrorHook()

    // Start batch timer
    this.startBatchTimer()

    // Handle page unload
    this.setupBeforeUnload()

    this.isSetup = true
    logger.info('[ErrorLogService] Error capture initialized')
  }

  /**
   * Load custom ignore patterns from Firestore
   * Note: This may fail if user isn't authenticated yet - that's OK, we'll use built-in patterns only
   */
  async loadIgnorePatterns() {
    try {
      const docRef = doc(db, 'settings', 'errorLogIgnorePatterns')
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        this.customIgnorePatterns = (data.patterns || [])
          .filter(p => p.enabled !== false)
          .map(p => ({
            ...p,
            // Convert string patterns to RegExp
            compiledPattern: new RegExp(p.pattern, 'i')
          }))
        logger.debug('[ErrorLogService] Loaded custom ignore patterns:', this.customIgnorePatterns.length)
      }
    } catch (error) {
      // This is expected if user isn't authenticated yet - custom patterns will just be empty
      // Built-in patterns will still work
      logger.debug('[ErrorLogService] Could not load custom ignore patterns (may not be authenticated):', error.code || error.message)
    }
    this.ignorePatternsFetched = true
  }

  /**
   * Check if error matches any ignore pattern
   */
  shouldIgnoreError(error, route) {
    const message = error.message || String(error)
    const stack = error.stack || ''

    // Check built-in patterns
    for (const pattern of BUILT_IN_IGNORE_PATTERNS) {
      if (this.matchesPattern(pattern, { message, stack, route })) {
        logger.debug('[ErrorLogService] Ignoring error (built-in pattern):', pattern.description)
        return true
      }
    }

    // Check custom patterns
    for (const pattern of this.customIgnorePatterns) {
      if (this.matchesPattern(pattern, { message, stack, route })) {
        logger.debug('[ErrorLogService] Ignoring error (custom pattern):', pattern.description)
        return true
      }
    }

    return false
  }

  /**
   * Check if error matches a single pattern
   */
  matchesPattern(patternConfig, errorContext) {
    const { type, pattern, compiledPattern } = patternConfig
    const regex = compiledPattern || (pattern instanceof RegExp ? pattern : new RegExp(pattern, 'i'))

    switch (type) {
      case 'message':
        return regex.test(errorContext.message)
      case 'route':
        return regex.test(errorContext.route)
      case 'stack':
        return regex.test(errorContext.stack)
      case 'source':
        return regex.test(errorContext.source || '')
      default:
        return false
    }
  }

  /**
   * Extract the core error message by stripping common prefixes and wrappers
   * This helps group errors from different sources (Vue, window, console) that are the same underlying error
   */
  extractCoreMessage(message) {
    if (!message) return ''

    let core = message

    // Remove common error prefixes
    const prefixPatterns = [
      /^Global Error:\s*/i,
      /^Uncaught\s+(Error|TypeError|ReferenceError|SyntaxError|RangeError):\s*/i,
      /^Error:\s*/i,
      /^TypeError:\s*/i,
      /^ReferenceError:\s*/i,
      /^SyntaxError:\s*/i,
      /^RangeError:\s*/i,
      /^\[[\w-]+\]\s*/,  // [ComponentName] prefix from logger
      /^%c.*?%c/,  // Logger style placeholders
    ]

    for (const pattern of prefixPatterns) {
      core = core.replace(pattern, '')
    }

    // Remove common suffixes that vary between captures
    core = core
      .replace(/\s*at\s+.*$/s, '')  // Remove "at function..." suffixes
      .replace(/\s*\(.*?\)$/s, '')  // Remove trailing parenthetical info
      .trim()

    // Normalize whitespace
    core = core.replace(/\s+/g, ' ')

    return core
  }

  /**
   * Extract the "error signature" - the unique part of an error that identifies the actual issue
   * This goes beyond extractCoreMessage to normalize variations that refer to the same error
   *
   * Focuses on grouping errors that differ only by:
   * - Component prefix: [App] vs [Auth] vs [Store]
   * - Synonymous verbs for the same operation: "load users" vs "list users", "fetch users" vs "get users"
   */
  extractErrorSignature(message) {
    let core = this.extractCoreMessage(message)

    // Normalize synonymous verb pairs that mean the same operation
    // "Failed to load users" and "Failed to list users" -> "Failed to fetch users" (canonical form)
    // "Failed to get users" and "Failed to fetch users" -> "Failed to fetch users"
    const verbNormalizations = [
      // load/list/fetch/get -> fetch (for data retrieval)
      { pattern: /^(Failed to |Error |Could not |Unable to |Cannot )(load|list|get|retrieve)\b/i, replacement: '$1fetch' },
      // save/store/persist/write -> save (for data persistence)
      { pattern: /^(Failed to |Error |Could not |Unable to |Cannot )(store|persist|write)\b/i, replacement: '$1save' },
      // delete/remove/destroy -> delete (for data removal)
      { pattern: /^(Failed to |Error |Could not |Unable to |Cannot )(remove|destroy|erase)\b/i, replacement: '$1delete' },
      // create/add/insert -> create (for data creation)
      { pattern: /^(Failed to |Error |Could not |Unable to |Cannot )(add|insert|make)\b/i, replacement: '$1create' },
      // update/modify/edit/change -> update (for data modification)
      { pattern: /^(Failed to |Error |Could not |Unable to |Cannot )(modify|edit|change|patch)\b/i, replacement: '$1update' },
    ]

    for (const { pattern, replacement } of verbNormalizations) {
      core = core.replace(pattern, replacement)
    }

    return core.trim()
  }

  /**
   * Generate fingerprint for deduplication
   * Uses normalized "core" message to group errors from different sources
   */
  generateFingerprint(error, route) {
    const message = error.message || String(error)

    // Extract core message for better grouping across sources
    const coreMessage = this.extractCoreMessage(message)

    // Get first few meaningful stack frames (skip noise frames)
    const stackLines = (error.stack || '').split('\n')
    const meaningfulFrames = stackLines
      .filter(line => {
        // Skip empty lines and the error message line
        if (!line.trim() || line.includes(message.substring(0, 30))) return false
        // Skip internal browser/framework frames
        if (line.includes('node_modules') && !line.includes('@/')) return false
        return true
      })
      .slice(0, 3)
      .join('')

    // Use simple hash instead of btoa to handle unicode
    // Fingerprint = core message + stack frames + route
    const input = `${coreMessage}:${meaningfulFrames}:${route}`
    let hash = 0
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36)
  }

  /**
   * Generate a "grouping key" for retroactive merging
   * Less strict than fingerprint - groups errors that are likely the same issue
   * Uses extractErrorSignature to normalize verb variations (e.g., "load users" vs "list users")
   */
  generateGroupingKey(message) {
    // Use error signature for better grouping across verb variations
    const signature = this.extractErrorSignature(message)

    // Create a simplified key that will match across variations
    const simplified = signature
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')  // Replace special chars with spaces
      .replace(/\s+/g, ' ')       // Normalize whitespace
      .trim()
      .substring(0, 100)          // Limit length for hashing

    let hash = 0
    for (let i = 0; i < simplified.length; i++) {
      const char = simplified.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return Math.abs(hash).toString(36)
  }

  /**
   * Check if running on localhost (development)
   */
  isLocalhost() {
    try {
      const hostname = window.location.hostname
      return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')
    } catch {
      return false
    }
  }

  /**
   * Capture error with full context
   */
  captureError(error, source, additionalContext = {}) {
    // Skip error logging on localhost
    if (this.isLocalhost()) return

    // Recursion guard
    if (this.isCapturing) return
    this.isCapturing = true

    try {
      const route = this.getCurrentRoute()

      // Check ignore patterns
      if (this.shouldIgnoreError(error, route)) {
        return
      }

      // Extract core message early for cross-source deduplication
      const message = error.message || String(error)
      const coreMessage = this.extractCoreMessage(message)

      // Short-term deduplication across different sources (Vue error handler vs console.error)
      // This catches the same error being reported by multiple handlers within a short window
      const now = Date.now()
      if (this.recentCoreMessages.has(coreMessage)) {
        const lastSeen = this.recentCoreMessages.get(coreMessage)
        if (now - lastSeen < this.recentMessageWindow) {
          // Same core error message seen very recently from another source - skip
          return
        }
      }
      this.recentCoreMessages.set(coreMessage, now)

      // Clean up old entries from recentCoreMessages periodically
      if (this.recentCoreMessages.size > 100) {
        const cutoff = now - this.recentMessageWindow
        for (const [msg, time] of this.recentCoreMessages) {
          if (time < cutoff) {
            this.recentCoreMessages.delete(msg)
          }
        }
      }

      // Rate limiting check
      if (!this.checkRateLimit()) {
        return
      }

      const fingerprint = this.generateFingerprint(error, route)

      // Deduplication - increment count if same error seen this session
      if (this.fingerprints.has(fingerprint)) {
        const existing = this.fingerprints.get(fingerprint)
        existing.occurrenceCount++
        existing.lastSeen = new Date().toISOString()
        return
      }

      const groupingKey = this.generateGroupingKey(message)

      const errorLog = {
        fingerprint,
        groupingKey, // Used for retroactive merging of similar errors
        message,
        coreMessage, // Normalized message for display (already extracted above)
        stack: this.truncateStack(error.stack || ''),
        source, // 'window', 'unhandled-rejection', 'vue', 'console.error'
        timestamp: new Date().toISOString(),
        occurrenceCount: 1,
        firstSeen: new Date().toISOString(),
        lastSeen: new Date().toISOString(),
        context: this.collectContext(additionalContext),
        breadcrumbs: [...this.breadcrumbs],
        consoleLogs: this.getRecentConsoleLogs(),
        status: 'new'
      }

      this.fingerprints.set(fingerprint, errorLog)
      this.errorQueue.push(errorLog)

      // Use console.log so it's always visible (logger.debug may be filtered)
      console.log('[ErrorLogService] Captured error:', source, error.message?.substring(0, 50), 'Queue size:', this.errorQueue.length)

      // Flush if queue is full
      if (this.errorQueue.length >= this.maxQueueSize) {
        this.flushQueue()
      }
    } catch (err) {
      // Never let error capture throw
      console.warn('[ErrorLogService] Error during capture:', err)
    } finally {
      this.isCapturing = false
    }
  }

  /**
   * Truncate stack trace to reasonable size
   */
  truncateStack(stack) {
    if (!stack) return ''
    const maxLength = 10000
    if (stack.length <= maxLength) return stack
    return stack.substring(0, maxLength) + '\n... (truncated)'
  }

  /**
   * Get recent console logs for context
   */
  getRecentConsoleLogs() {
    try {
      const logs = consoleInterceptor.getConsoleLogs()
      // Get last 20 logs, truncate long messages
      return logs.slice(-20).map(log => ({
        ...log,
        message: log.message?.substring(0, 500) || ''
      }))
    } catch {
      return []
    }
  }

  /**
   * Collect rich context about the error
   */
  collectContext(additional = {}) {
    // Get user/company info from stores (lazy load to avoid circular deps)
    let userId = null
    let companyId = null
    let userEmail = null

    try {
      // Access Pinia store state - the pinia instance is exposed as window.__PINIA__
      const piniaState = window.__PINIA__?.state?.value
      if (piniaState?.userAuth) {
        // The userAuth store has a 'user' object with id, email, companyId, activeCompanyId
        const userAuthState = piniaState.userAuth
        userId = userAuthState.user?.id || null
        userEmail = userAuthState.user?.email || null
        companyId = userAuthState.user?.companyId || userAuthState.user?.activeCompanyId || null
      }
    } catch {
      // Ignore errors accessing stores
    }

    // Check for impersonation
    let isImpersonating = false
    let impersonatedCompanyId = null

    try {
      const piniaState = window.__PINIA__?.state?.value
      if (piniaState?.viewMode) {
        const viewModeState = piniaState.viewMode
        isImpersonating = viewModeState.isImpersonating || false
        impersonatedCompanyId = viewModeState.impersonatedCompany?.id || viewModeState.impersonatedCompany || null
      }
    } catch {
      // Ignore errors accessing stores
    }

    // Get app version from global
    let appVersion = null
    try {
      if (typeof __APP_VERSION__ !== 'undefined') {
        appVersion = __APP_VERSION__
      }
    } catch {
      // Ignore
    }

    return {
      // User context
      userId,
      userEmail,
      companyId,
      isImpersonating,
      impersonatedCompanyId,

      // Page context
      route: this.getCurrentRoute(),
      pageUrl: window.location.href,
      referrer: document.referrer || null,

      // Browser context
      browser: this.getBrowserInfo(),
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      deviceType: this.getDeviceType(),
      userAgent: navigator.userAgent,

      // App context
      appVersion,

      // Additional context
      ...additional
    }
  }

  /**
   * Get current route path
   */
  getCurrentRoute() {
    try {
      return window.location.pathname + window.location.hash
    } catch {
      return 'unknown'
    }
  }

  /**
   * Get browser info string
   */
  getBrowserInfo() {
    const ua = navigator.userAgent
    let browser = 'Unknown'
    let version = ''

    if (ua.includes('Firefox/')) {
      browser = 'Firefox'
      version = ua.match(/Firefox\/(\d+)/)?.[1] || ''
    } else if (ua.includes('Edg/')) {
      browser = 'Edge'
      version = ua.match(/Edg\/(\d+)/)?.[1] || ''
    } else if (ua.includes('Chrome/')) {
      browser = 'Chrome'
      version = ua.match(/Chrome\/(\d+)/)?.[1] || ''
    } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
      browser = 'Safari'
      version = ua.match(/Version\/(\d+)/)?.[1] || ''
    }

    let os = 'Unknown OS'
    if (ua.includes('Mac OS')) os = 'macOS'
    else if (ua.includes('Windows')) os = 'Windows'
    else if (ua.includes('Linux')) os = 'Linux'
    else if (ua.includes('Android')) os = 'Android'
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'

    return `${browser} ${version} on ${os}`
  }

  /**
   * Get device type based on screen width
   */
  getDeviceType() {
    const width = window.innerWidth
    if (width < 768) return 'mobile'
    if (width < 1024) return 'tablet'
    return 'desktop'
  }

  /**
   * Rate limiting check
   */
  checkRateLimit() {
    this.rateLimitCounter++

    // Reset counter after window
    setTimeout(() => {
      this.rateLimitCounter = Math.max(0, this.rateLimitCounter - 1)
    }, this.rateLimitWindow)

    if (this.rateLimitCounter > this.maxErrorsPerWindow) {
      if (this.rateLimitCounter === this.maxErrorsPerWindow + 1) {
        logger.warn('[ErrorLogService] Rate limit exceeded, throttling error capture')
      }
      return false
    }
    return true
  }

  /**
   * Setup window.onerror handler
   */
  setupWindowErrorHandler() {
    const originalOnError = window.onerror

    window.onerror = (message, source, lineno, colno, error) => {
      // Call original if exists
      if (originalOnError) {
        originalOnError(message, source, lineno, colno, error)
      }

      // Create error object if not provided
      const errorObj = error || new Error(message)
      if (!errorObj.stack && source) {
        errorObj.stack = `at ${source}:${lineno}:${colno}`
      }

      this.captureError(errorObj, 'window', {
        sourceFile: source,
        lineNumber: lineno,
        columnNumber: colno
      })

      // Don't prevent default error handling
      return false
    }
  }

  /**
   * Setup unhandled promise rejection handler
   */
  setupUnhandledRejectionHandler() {
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason))

      this.captureError(error, 'unhandled-rejection')
    })
  }

  /**
   * Setup console.error hook via consoleInterceptor
   */
  setupConsoleErrorHook() {
    // Check if consoleInterceptor has the onError method
    if (typeof consoleInterceptor.onError === 'function') {
      this.consoleErrorUnsubscribe = consoleInterceptor.onError((args, logEntry) => {
        // Convert console.error arguments to an error
        const firstArg = args[0]
        let error

        if (firstArg instanceof Error) {
          error = firstArg
        } else {
          // Extract meaningful message from console.error args
          // Logger format: %c[timestamp] %c[LEVEL] <style1> <style2> [Component] message
          const message = this.extractCleanMessage(args)
          error = new Error(message)
          // Use the stack from logEntry if available
          if (logEntry?.stack) {
            error.stack = logEntry.stack
          }
        }

        // Preserve structured payload args (e.g. `logger.error('msg', { errorCode, serverResponse })`)
        // so queryable fields like serverResponse/errorCode survive into Firestore instead of
        // being collapsed into a string summary.
        const payload = this.extractStructuredPayload(args)
        const additionalContext = payload ? { payload } : {}

        this.captureError(error, 'console.error', additionalContext)
      })
    }
  }

  /**
   * Extract structured payload objects from console.error arguments.
   * Filters out CSS style strings (from logger's %c formatting), Error instances,
   * and primitive strings — leaving only the plain-object contexts that callers
   * pass as the second/third arg to logger.error().
   *
   * Returns the merged payload object, or null if none found.
   */
  extractStructuredPayload(args) {
    const objects = []
    for (const arg of args) {
      if (arg === null || arg === undefined) continue
      if (arg instanceof Error) continue
      if (typeof arg !== 'object') continue
      // Skip DOM nodes / things that would serialize poorly
      if (typeof window !== 'undefined' && arg instanceof window.Node) continue
      objects.push(arg)
    }
    if (objects.length === 0) return null

    // Shallow-clone and sanitize each object: drop functions, cap string length,
    // prevent huge structures from blowing up the Firestore doc.
    const sanitize = (obj) => {
      const out = {}
      for (const [key, value] of Object.entries(obj)) {
        if (typeof value === 'function') continue
        if (typeof value === 'string') {
          out[key] = value.length > 2000 ? value.slice(0, 2000) + '…(truncated)' : value
        } else if (value === null || ['number', 'boolean'].includes(typeof value)) {
          out[key] = value
        } else {
          // Stringify objects/arrays once, capped
          try {
            const str = JSON.stringify(value)
            out[key] = str.length > 2000 ? str.slice(0, 2000) + '…(truncated)' : str
          } catch {
            out[key] = String(value).slice(0, 500)
          }
        }
      }
      return out
    }

    return objects.length === 1
      ? sanitize(objects[0])
      : Object.assign({}, ...objects.map(sanitize))
  }

  /**
   * Extract clean message from console.error arguments
   * Handles logger-formatted messages with %c styling
   */
  extractCleanMessage(args) {
    // Filter out style strings (they contain CSS properties like 'color:', 'background:')
    const meaningfulArgs = args.filter(arg => {
      if (typeof arg !== 'string') return true
      // Skip if it looks like a CSS style string
      if (arg.includes('color:') && arg.includes(';')) return false
      if (arg.includes('background:') && arg.includes(';')) return false
      if (arg.includes('font-weight:') && arg.includes(';')) return false
      if (arg.includes('padding:') && arg.includes(';')) return false
      return true
    })

    // Join remaining args and clean up %c placeholders
    let message = meaningfulArgs.map(arg => {
      if (typeof arg === 'string') {
        // Remove %c placeholders and timestamps like [2025-12-17T...]
        return arg
          .replace(/%c/g, '')
          .replace(/\[\d{4}-\d{2}-\d{2}T[^\]]+\]/g, '')
          .replace(/\[(DEBUG|INFO|WARN|ERROR)\]/g, '')
          .trim()
      }
      if (arg instanceof Error) {
        return arg.message
      }
      if (arg === null) {
        return 'null'
      }
      if (arg === undefined) {
        return 'undefined'
      }
      if (typeof arg === 'object') {
        return this.extractObjectMessage(arg)
      }
      return String(arg)
    }).filter(s => s && s !== '[object Object]').join(' ')

    return message.trim() || 'Unknown error'
  }

  /**
   * Extract meaningful message from an object
   */
  extractObjectMessage(obj) {
    if (!obj) return ''

    // Handle Error objects
    if (obj instanceof Error) {
      return obj.message || obj.name || 'Error'
    }

    // Check common error property patterns
    if (obj.error) {
      if (typeof obj.error === 'string') return obj.error
      if (obj.error.message) return obj.error.message
      if (obj.error instanceof Error) return obj.error.message
    }
    if (obj.message) {
      return typeof obj.message === 'string' ? obj.message : String(obj.message)
    }
    if (obj.msg) {
      return typeof obj.msg === 'string' ? obj.msg : String(obj.msg)
    }
    if (obj.reason) {
      return typeof obj.reason === 'string' ? obj.reason : String(obj.reason)
    }
    // Structured logger payload convention: { errorCode, errorMessage, serverResponse, ... }
    if (obj.errorMessage || obj.errorCode) {
      const parts = []
      if (obj.errorCode) parts.push(String(obj.errorCode))
      if (obj.errorMessage) parts.push(String(obj.errorMessage))
      return parts.join(' ') || 'Error'
    }
    if (obj.code) {
      return `Error code: ${obj.code}`
    }

    // No error-message-like key matched. Don't synthesize a summary from the object —
    // unique values (fileId, progress, counts, etc.) would poison the message fingerprint
    // and prevent grouping. The full structured payload is captured separately on the
    // error record via extractStructuredPayload → context.payload.
    return ''
  }

  /**
   * Setup breadcrumb tracking for user actions
   */
  setupBreadcrumbTracking() {
    // Track navigation
    const originalPushState = history.pushState
    const originalReplaceState = history.replaceState

    history.pushState = (...args) => {
      this.addBreadcrumb('navigation', `Navigate to ${args[2]}`)
      return originalPushState.apply(history, args)
    }

    history.replaceState = (...args) => {
      this.addBreadcrumb('navigation', `Replace state to ${args[2]}`)
      return originalReplaceState.apply(history, args)
    }

    window.addEventListener('popstate', () => {
      this.addBreadcrumb('navigation', `Back/forward to ${window.location.pathname}`)
    })

    // Track clicks on interactive elements (debounced)
    let lastClickTime = 0
    document.addEventListener('click', (event) => {
      const now = Date.now()
      if (now - lastClickTime < 100) return // Debounce
      lastClickTime = now

      const target = event.target
      if (!target) return

      // Get a useful description of what was clicked
      const tagName = target.tagName?.toLowerCase()
      if (!['button', 'a', 'input', 'select'].includes(tagName) &&
          !target.closest('button') &&
          !target.closest('a')) {
        return // Only track interactive elements
      }

      // className can be SVGAnimatedString for SVG elements, so convert to string first
      const className = typeof target.className === 'string'
        ? target.className
        : target.className?.baseVal || ''

      const text = target.textContent?.trim().substring(0, 50) ||
                   target.getAttribute('aria-label') ||
                   target.getAttribute('title') ||
                   className.split(' ')[0] ||
                   tagName

      this.addBreadcrumb('click', `Click on "${text}"`, {
        tagName,
        id: target.id || null,
        className: className.substring(0, 50) || null
      })
    }, { capture: true, passive: true })
  }

  /**
   * Add a breadcrumb entry
   */
  addBreadcrumb(type, message, data = null) {
    this.breadcrumbs.push({
      type,
      message,
      timestamp: new Date().toISOString(),
      data
    })

    // Keep only recent breadcrumbs
    if (this.breadcrumbs.length > this.maxBreadcrumbs) {
      this.breadcrumbs.shift()
    }
  }

  /**
   * Setup page unload handler to flush remaining errors
   */
  setupBeforeUnload() {
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushQueueSync()
      }
    })

    window.addEventListener('pagehide', () => {
      this.flushQueueSync()
    })
  }

  /**
   * Start the batch timer
   */
  startBatchTimer() {
    this.batchTimer = setInterval(() => {
      this.flushQueue()
    }, this.batchInterval)
  }

  /**
   * Flush error queue to Firestore (async)
   */
  async flushQueue() {
    if (this.errorQueue.length === 0) return

    // firestore.rules requires request.auth != null to create an errorLogs doc, so a
    // flush while signed out is guaranteed to fail — and its permission-denied noise
    // buries the real error on public pages (login/signup/invite). Return before
    // draining the queue so these flush on a later pass once authenticated.
    if (!auth.currentUser) return

    console.log('[ErrorLogService] Flushing queue with', this.errorQueue.length, 'errors')

    const errorsToSend = [...this.errorQueue]
    this.errorQueue = []

    try {
      await this.sendToFirestore(errorsToSend)
      console.log('[ErrorLogService] Flushed', errorsToSend.length, 'errors to Firestore')
    } catch (error) {
      // Put errors back in queue on failure
      this.errorQueue.unshift(...errorsToSend)
      console.warn('[ErrorLogService] Failed to flush errors:', error)
    }
  }

  /**
   * Flush error queue synchronously (for page unload)
   * Uses sendBeacon for reliability
   */
  flushQueueSync() {
    if (this.errorQueue.length === 0) return
    if (!auth.currentUser) return

    const errorsToSend = [...this.errorQueue]
    this.errorQueue = []

    // Note: sendBeacon doesn't work directly with Firestore
    // We'll try the async method, which may or may not complete
    this.sendToFirestore(errorsToSend).catch(() => {
      // If this fails on unload, we lose the errors - acceptable tradeoff
    })
  }

  /**
   * Send errors to Firestore with cross-session deduplication
   * If an error with the same groupingKey exists on the same route, update it instead of creating new
   * Uses groupingKey (normalized message hash) instead of fingerprint because fingerprints
   * include stack frames with build-specific chunk names that change between deployments
   * Note: Deduplication requires read access (admin/super only), regular users will create new docs
   */
  async sendToFirestore(errors) {
    if (errors.length === 0) return

    const errorLogsRef = collection(db, 'errorLogs')

    for (const errorLog of errors) {
      try {
        let dedupSucceeded = false

        // Try to deduplicate - this requires read permission (admin/super only)
        // Use groupingKey + route for matching (more stable than fingerprint across deployments)
        try {
          // First try exact groupingKey match on the same route
          const q = query(
            errorLogsRef,
            where('groupingKey', '==', errorLog.groupingKey),
            where('context.route', '==', errorLog.context?.route || '')
          )
          const querySnapshot = await getDocs(q)

          if (!querySnapshot.empty) {
            // Update existing document - increment occurrence count and update lastSeen
            const existingDoc = querySnapshot.docs[0]
            await updateDoc(existingDoc.ref, {
              occurrenceCount: increment(errorLog.occurrenceCount || 1),
              lastSeen: errorLog.lastSeen || new Date().toISOString(),
              updatedAt: serverTimestamp(),
              // Update context with latest user info (helpful to see who else hit this)
              'context.lastSeenBy': errorLog.context?.userId || null,
              'context.lastSeenByEmail': errorLog.context?.userEmail || null,
              // Repeat occurrences otherwise keep only the FIRST hit's payload —
              // a different item/user hitting the same message was invisible.
              'context.lastPayload': this.cleanData(errorLog.context?.payload) || null,
              'context.lastBrowser': errorLog.context?.browser || null
            })
            dedupSucceeded = true
            console.log('[ErrorLogService] Deduplicated error by groupingKey:', errorLog.coreMessage?.substring(0, 50))
          }
        } catch (dedupErr) {
          // Dedup query failed (likely permission denied for non-admin users, or missing index)
          // Fall through to create new document
          console.log('[ErrorLogService] Dedup query failed:', dedupErr.code || dedupErr.message)
        }

        if (!dedupSucceeded) {
          // Create new document
          const docRef = doc(errorLogsRef)
          await setDoc(docRef, {
            ...this.cleanData(errorLog),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          })
          console.log('[ErrorLogService] Created new error log:', errorLog.message?.substring(0, 50))
        } else {
          console.log('[ErrorLogService] Updated existing error log (dedup):', errorLog.message?.substring(0, 50))
        }
      } catch (err) {
        // Log but don't throw - continue with other errors
        console.warn('[ErrorLogService] Failed to save error:', err.code || err.message, err)
      }
    }
  }

  /**
   * Clean data for Firestore (remove undefined values)
   */
  cleanData(obj) {
    if (obj === null || obj === undefined) return null
    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanData(item)).filter(item => item !== undefined)
    }
    if (typeof obj === 'object') {
      const cleaned = {}
      for (const [key, value] of Object.entries(obj)) {
        const cleanedValue = this.cleanData(value)
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue
        }
      }
      return cleaned
    }
    return obj
  }

  /**
   * Manually capture an error (for use in catch blocks)
   */
  capture(error, additionalContext = {}) {
    this.captureError(error, 'manual', additionalContext)
  }

  /**
   * Cleanup on service destruction
   */
  destroy() {
    if (this.batchTimer) {
      clearInterval(this.batchTimer)
    }
    if (this.consoleErrorUnsubscribe) {
      this.consoleErrorUnsubscribe()
    }
    this.flushQueue()
  }
}

export const errorLogService = new ErrorLogService()
